<template>
  <div class="edit-user-wrapper">
    <p class="title">{{ $t('app.panels.createUser.title') }}</p>
    <div class="separator"></div>
    <div class="subtitle">{{ $t('app.panels.createUser.subtitle') }}</div>
    <transition v-if="!selectRoleStep" name="fade" mode="out-in">
      <div class="pii">
        <VForm :callback="validatePii" :submitButton="{
          label: 'Suivant',
          class: 'm green',
        }">
          <template #form-body>
            <div class="inline">
              <VField class="field m" v-model="lastName" name="name" :label="$t('other.form.lastName')" placeholder="Dupont" rules="required|textOnly"></VField>
              <VField class="field m" v-model="firstName" name="first_name" :label="$t('other.form.firstName')" placeholder="Bernard" rules="required|textOnly"></VField>
            </div>
            <VField type="email" name="email" v-model="email" :label="$t('other.form.email')" placeholder="bernard.dupont@gmail.com" rules="required|email" class="m"></VField>
          </template>
        </VForm>
      </div>
    </transition>
    <transition v-else name="fade" mode="out-in">
      <div class="role-selection">
        <LabeledSelect
          id="role"
          class="m"
          name="role"
          :label="$t('app.panels.createUser.role')"
          :model-value="selectedRole"
          :placeholder="'Role'"
          :searchable="false"
          :clearable="false"
          :options="filteredOrganisationRoles"
          @update:modelValue="updateRole"
        >
          <template #selected-option="option: AppRole">
            <div class="xs-text">
              {{ $t(`app.company.members.${option.name}`) }}
            </div>
          </template>
          <template #option="option: AppRole">
            <div class="xs-text">
              {{ $t(`app.company.members.${option.name}`) }}
            </div>
          </template>
        </LabeledSelect>
        <ScopeSelector v-if="selectedRole" :scopes="selectedScopes" :role="selectedRole" @selected-scopes="selectedScopes = $event"></ScopeSelector>
        <div class="inline">
          <Button class="m green" :label="$t('other.actions.confirm')" @click="selectRoleStep = true; createUser()"></Button>
          <Button class="m outline-white" :label="$t('other.actions.cancel')" @click="selectRoleStep = false"></Button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import { useAllOrgnisationRoles } from '@/queries/organisation.query';
import { useInviteInOrganisation } from '@/queries/invitation.query';

import { AppRole, MemberRole } from '@/types/role';
import { ScopeRef } from '@/types/member';

import Button from '@/components/global/Button.vue';
import VField from '@/components/global/VField.vue';
import VForm from '@/components/global/VForm.vue';
import ScopeSelector from './ScopeSelector.vue';
import LabeledSelect from '@/components/global/LabeledSelect.vue';

const emit = defineEmits<{
  (e: 'close'): void,
}>();

const { t } = useI18n();
const toast = useToast();
const { data: organisationRoles, isPending: isOrganisationRolesPending } = useAllOrgnisationRoles();
const { mutate: inviteInOrganisation } =  useInviteInOrganisation();

const firstName = ref("");
const lastName = ref("");
const email = ref("");

const selectRoleStep = ref(false);
const selectedRole: Ref<AppRole> = ref(undefined);
const selectedScopes: Ref<ScopeRef[]> = ref([]);

const filteredOrganisationRoles = computed(() => {
  if (isOrganisationRolesPending.value) return [];
  return organisationRoles.value.filter((role) => {
    if (role.role_backward_compatibility_id === MemberRole.PARTCIPANT) return false;
    return true;
  })
});

function updateRole(to: AppRole) {
  selectedRole.value = to;
}

function validatePii() {
  selectRoleStep.value = true;
}

function createUser() {
  inviteInOrganisation({
    email: email.value,
    firstName: firstName.value,
    lastName: lastName.value,
    roleId: selectedRole.value.id,
    scopes: selectedScopes.value,
    sendEmail: true,
  }, {
    onSuccess: () => {
      toast.success(t('app.toasts.success'));
      emit('close');
    }
  });
}

</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.edit-user-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;

  height: 100%;
  width: 100%;
  max-width: 40vw;

  .inline {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .title {
    font-size: 20px;
    font-weight: 700;
  }

  .subtitle {
    font-size: 14px;
    font-weight: 600;
  }

  .separator {
    width: 100%;
    height: 1px;
    background-color: var(--light-grey-color);
  }

  #role {
    width: 50%;
  }

  .pii {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .field {
      width: 100%;
    }
  }

  .label {
    font-size: 12px;
    color: var(--label-color);
    margin-bottom: 6px;
  }

  .role-selection {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}
</style>